import React from 'react';
import './PlannerMapIntervals.scss';
import {
  OrderListOrderClazz,
  TimeInterval,
} from '../../../../types/orderTypes';
import { BundleJson } from '../../../../types/bundleTypes';
import { filterNotInBundles } from '../../PlannerMap';

interface OrderCount {
  notInBundle: number;
  inBundle: number;
  total: number;
}

interface OrderCountInterval {
  key: string;
  interval: TimeInterval;
  orderCount: OrderCount;
}

function addTo(a: OrderCount, b: OrderCount) {
  a.notInBundle += b.notInBundle;
  a.inBundle += b.inBundle;
  a.total += b.total;
}

function countOrders(orders, bundles) {
  const ordersIdsNotInBundles = filterNotInBundles(orders, bundles).map(
    order => order.id
  );
  const countedOrders: OrderCountInterval[] = orders
    .sort((first, second) => {
      return first.interval().from.unix() - second.interval().from.unix();
    })
    .reduce(
      (
        accumulatedList: OrderCountInterval[],
        currentValue: OrderListOrderClazz
      ) => {
        const isNotInBundle = ordersIdsNotInBundles.includes(currentValue.id);
        const orderCountInterval: OrderCountInterval = {
          key: `${currentValue.interval().from.toString()}-${currentValue
            .interval()
            .to.toString()}`,
          interval: currentValue.interval(),
          orderCount: {
            notInBundle: isNotInBundle ? 1 : 0,
            inBundle: isNotInBundle ? 0 : 1,
            total: 1,
          },
        };

        const existingEntry = accumulatedList.findIndex(
          value => value.key === orderCountInterval.key
        );
        if (existingEntry !== -1) {
          addTo(
            accumulatedList[existingEntry].orderCount,
            orderCountInterval.orderCount
          );
        } else {
          accumulatedList.push(orderCountInterval);
        }
        return accumulatedList;
      },
      []
    );
  return countedOrders;
}

function summarizeCounts(countedOrders) {
  return countedOrders.reduce(
    (sum: OrderCount, currentValue: OrderCountInterval) => {
      addTo(sum, currentValue.orderCount);
      return sum;
    },
    { notInBundle: 0, inBundle: 0, total: 0 }
  );
}

export function isSameInterval(
  intervalOne: TimeInterval,
  intervalTwo: TimeInterval
): boolean {
  return (
    intervalOne.to.isSame(intervalTwo.to) &&
    intervalOne.from.isSame(intervalTwo.from)
  );
}

export function isIntervalInList(
  selectedTimeIntervals: TimeInterval[],
  timeInterval: TimeInterval
): boolean {
  return selectedTimeIntervals.some(interval =>
    isSameInterval(interval, timeInterval)
  );
}

export default function PlannerMapInterval({
  onTimeIntervalSelected,
  selectedTimeIntervals,
  orders,
  bundles,
}: {
  onTimeIntervalSelected: (interval: TimeInterval) => void;
  selectedTimeIntervals: TimeInterval[];
  orders: OrderListOrderClazz[];
  bundles: BundleJson[];
}): JSX.Element {
  const countedOrders = countOrders(orders, bundles);
  const totalOrderCount = summarizeCounts(countedOrders);

  return (
    <div className="planner-map__interval-filter">
      <div style={{ height: 5 }} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'baseline',
        }}
      >
        <div style={{ width: '130px' }} />
        <div style={{ width: '80px', textAlign: 'right' }}>
          Total: {totalOrderCount.total}
        </div>
        <div style={{ width: '80px', textAlign: 'right' }}>
          In: {totalOrderCount.inBundle}
        </div>
        <div style={{ width: '80px', textAlign: 'right' }}>
          Not: {totalOrderCount.notInBundle}
        </div>
      </div>
      {countedOrders.map(orderCountInterval => {
        const interval = orderCountInterval.interval;
        const intervalId = `${orderCountInterval.key}`;
        return (
          <div key={intervalId}>
            <label htmlFor={intervalId}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                }}
              >
                <input
                  style={{
                    width: '16px',
                    marginRight: '2px',
                    marginLeft: '2px',
                  }}
                  type="checkbox"
                  id={intervalId}
                  checked={isIntervalInList(selectedTimeIntervals, interval)}
                  onChange={() => {
                    return onTimeIntervalSelected(interval);
                  }}
                />
                <div style={{ width: '110px' }}>
                  {interval.from.format('HH:mm')} -{' '}
                  {interval.to.format('HH:mm')}
                </div>
                <div style={{ width: '80px', textAlign: 'right' }}>
                  {orderCountInterval.orderCount.total}
                </div>
                <div style={{ width: '80px', textAlign: 'right' }}>
                  {orderCountInterval.orderCount.inBundle}
                </div>
                <div style={{ width: '80px', textAlign: 'right' }}>
                  {orderCountInterval.orderCount.notInBundle}
                </div>
              </div>
            </label>
          </div>
        );
      })}
    </div>
  );
}
